import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { FieldGroup, FormBuilder } from "react-reactive-form";
import EfficacyAnalysisQuestions from "../../questions/EfficacyAnalysisQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import RedirectHandler from "../../components/RedirectHandler";
import { useParams } from "react-router-dom";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";

const isProduction = process.env.NODE_ENV === "production";

const EfficacyAnalysis = () => {
  const { studyId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacySensitivityNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacySensitivityNames: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
  };

  // Default empty-group structures for each array control, matching the
  // inputTextBoxes definitions used by QuestionsAnswersArray on this page.
  const defaultGroupTemplates = {
    qPrimaryEfficacyComparisonNames: { Treatment: "", Comparator: "" },
    qTgNamesComparison: { Treatment: "", Comparator: "" },
  };

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setRestoreVersion] = useState(0);
  const isRestoringRef = useRef(false);
  const lastRestoredQuestionsDataRef = useRef(null);

  useEffect(() => {
    if (!questionsDataSaved) {
      setAjaxLoading(true);
      CsrAPI.getQuestions({ studyId })
        .then(questionsDataSaved => {
          replaceNullArrays(questionsDataSaved);
          setQuestionsDataSaved(questionsDataSaved);
          setErrMsg("");
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else if (error.response?.status === 403) {
            setShowRedirect(true);
          } else {
            setErrMsg("Failed to get questions");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    }
  }, [studyId, setQuestionsDataSaved, questionsDataSaved]);

  const replaceNullArrays = obj => {
    for (let key in obj) {
      if (typeof obj[key] === "object") {
        if (Array.isArray(obj[key])) {
          if (obj[key].every(value => value === null)) {
            obj[key] = [];
          }
        } else {
          replaceNullArrays(obj[key]);
        }
      }
    }
  };

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      // Prevent subscription handlers from resetting fields during restore
      isRestoringRef.current = true;

      const wizardFormValue = questionsDataSaved.wizardForm;

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (formArraysKeys[key] && wizardForm.controls[key]) {
          while (wizardForm.controls[key].length > 0) {
            wizardForm.controls[key].removeAt(0);
          }
        }
      });

      // Monotonically increasing key so restored groups always get a fresh
      // React key, forcing FieldControl to remount and read the new value.
      let restoreKeyIndex = 0;

      Object.keys(wizardFormValue).map(function (key) {
        if (formArraysKeys[key]) {
          if (formArraysKeys[key] === "object") {
            wizardFormValue[key].forEach(item => {
              const group = FormBuilder.group(item);
              group.meta = { key: `r${++restoreKeyIndex}` };
              wizardForm.controls[key]?.push(group);
            });
            // If the saved array was empty, ensure at least one visible
            // form control so the user doesn't see only "Add another".
            if (wizardFormValue[key].length === 0 && wizardForm.controls[key]) {
              const template = defaultGroupTemplates[key] || {
                valueHelper: "",
              };
              const emptyGroup = FormBuilder.group(
                Object.fromEntries(Object.keys(template).map(k => [k, [""]]))
              );
              emptyGroup.meta = { key: `r${++restoreKeyIndex}` };
              wizardForm.controls[key].push(emptyGroup);
            }
          } else {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key].push(FormBuilder.control(item));
            });
          }
        } else {
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Mark the form as pristine so that subscription handlers
      // (onChangePrimaryEfficacy) don't treat restored values as user
      // edits and reset the child efficacy fields.
      wizardForm.markAsPristine();

      isRestoringRef.current = false;

      setRestoreVersion(v => v + 1);
    }
  };

  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setShowRedirect,
    setIsSaving,
    questionsRef: null,
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <form name={"wizardForm"}>
                    <EfficacyAnalysisQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      isRestoringRef={isRestoringRef}
                    />
                  </form>
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Answer the following questions related to Efficacy"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Study Design & Demographics",
        buttonGoToAction: "/csr/" + studyId + "/study-design-demographics",
      }}
      nextButton={{
        buttonTitle: "Safety",
        buttonGoToAction: "/csr/" + studyId + "/safety-analysis",
      }}
      saveButtonHandler={handleSubmit}
      saveButton={true}
    />
  );
};

export default EfficacyAnalysis;
